// Orden y encendido de las secciones de la página pública.
//
// Hasta acá el orden vivía cableado en el JSX de RestaurantMicrosite: todos los
// comercios abrían igual. Un asador que vive de su parrilla y una cafetería que
// vive de sus reseñas no tienen por qué contar su página en el mismo orden.
//
// Este módulo es PURO a propósito (sin React): así el orden se puede testear de
// verdad y el panel puede usar la misma resolución que el render, sin que se
// desincronicen.

/** Secciones que el merchant puede mover o apagar. */
export const SECTION_IDS = [
  'promo',
  'featured',
  'menu',
  'reservations',
  'catering',
  'delivery',
  'about',
  'gallery',
  'reviews',
  'location',
  'social',
] as const

export type SectionId = (typeof SECTION_IDS)[number]

/**
 * El orden que la página tiene HOY. Es el default: un comercio que nunca tocó
 * esto tiene que ver exactamente lo mismo que antes de esta feature.
 */
export const DEFAULT_SECTION_ORDER: SectionId[] = [
  'promo',
  'featured',
  'menu',
  'reservations',
  'catering',
  'delivery',
  'about',
  'gallery',
  'reviews',
  'location',
  'social',
]

/**
 * El menú NO se puede apagar. Esto es una página de ventas: sin carta no hay
 * nada que vender, y un merchant que lo apague por error se queda sin negocio
 * online hasta que alguien lo note. El panel además ni ofrece el interruptor.
 */
export const REQUIRED_SECTIONS: SectionId[] = ['menu']

export type SectionPref = { id: SectionId; enabled: boolean }

const KNOWN = new Set<string>(SECTION_IDS)

/** Preferencia por defecto: el orden de siempre, todo encendido. */
export function defaultSectionPrefs(): SectionPref[] {
  return DEFAULT_SECTION_ORDER.map((id) => ({ id, enabled: true }))
}

/**
 * Normaliza lo que haya guardado el merchant (jsonb, o sea: cualquier cosa).
 *
 * Reglas, en este orden:
 *  1. Nada guardado / basura ⇒ el default exacto.
 *  2. Se descartan ids desconocidos y repetidos — una sección puede
 *     desaparecer en una versión futura y su rastro no debe romper el render.
 *  3. Las secciones que falten se INSERTAN en su lugar por defecto (después de
 *     la vecina anterior que sí esté), no al final: si mañana se agrega una
 *     sección, el comercio que ya ordenó las suyas tiene que verla donde
 *     corresponde, no colgando abajo de todo.
 *  4. Las obligatorias quedan encendidas, aunque el guardado diga lo contrario.
 */
export function resolveSections(raw: unknown): SectionPref[] {
  if (!Array.isArray(raw) || raw.length === 0) return defaultSectionPrefs()

  const seen = new Set<SectionId>()
  const out: SectionPref[] = []
  for (const entry of raw) {
    const id = (entry as { id?: unknown } | null)?.id
    if (typeof id !== 'string' || !KNOWN.has(id) || seen.has(id as SectionId)) continue
    seen.add(id as SectionId)
    out.push({
      id: id as SectionId,
      // Solo un `false` explícito apaga: un jsonb viejo sin el campo se
      // interpreta como encendido, que es el estado que tenía la página.
      enabled: (entry as { enabled?: unknown }).enabled !== false,
    })
  }
  if (out.length === 0) return defaultSectionPrefs()

  // Faltantes, cada una en su lugar por defecto.
  for (let i = 0; i < DEFAULT_SECTION_ORDER.length; i++) {
    const id = DEFAULT_SECTION_ORDER[i]
    if (seen.has(id)) continue
    // Se busca hacia atrás la vecina por defecto más cercana que ya esté puesta
    // y se inserta justo después; si no hay ninguna, va al principio.
    let at = 0
    for (let j = i - 1; j >= 0; j--) {
      const prev = DEFAULT_SECTION_ORDER[j]
      const idx = out.findIndex((s) => s.id === prev)
      if (idx !== -1) {
        at = idx + 1
        break
      }
    }
    out.splice(at, 0, { id, enabled: true })
    seen.add(id)
  }

  for (const required of REQUIRED_SECTIONS) {
    const found = out.find((s) => s.id === required)
    if (found) found.enabled = true
  }
  return out
}

/** ¿Esta sección se puede apagar desde el panel? */
export function isSectionRequired(id: SectionId): boolean {
  return REQUIRED_SECTIONS.includes(id)
}

/** Mueve una sección un lugar arriba (-1) o abajo (+1). Fuera de rango, no hace nada. */
export function moveSection(prefs: SectionPref[], id: SectionId, delta: -1 | 1): SectionPref[] {
  const from = prefs.findIndex((s) => s.id === id)
  if (from === -1) return prefs
  const to = from + delta
  if (to < 0 || to >= prefs.length) return prefs
  const next = [...prefs]
  ;[next[from], next[to]] = [next[to], next[from]]
  return next
}
